import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, MessageCircle, Calendar, Clock, Filter, Plus } from "lucide-react";
import { format } from "date-fns";
import SupportTicketForm from "./SupportTicketForm";

interface SupportTicket {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  is_anonymous: boolean;
  created_at: string;
  updated_at: string;
}

interface TicketResponse {
  id: string;
  message: string;
  is_internal_note: boolean;
  created_at: string;
}

const statusColors = {
  open: "bg-blue-500/10 text-blue-500",
  in_progress: "bg-yellow-500/10 text-yellow-500",
  pending_user: "bg-orange-500/10 text-orange-500",
  resolved: "bg-green-500/10 text-green-500",
  closed: "bg-gray-500/10 text-gray-500"
};

const priorityColors = {
  low: "bg-green-500/10 text-green-500",
  medium: "bg-yellow-500/10 text-yellow-500",
  high: "bg-orange-500/10 text-orange-500",
  urgent: "bg-red-500/10 text-red-500"
};

const categoryLabels = {
  technical: "Technical",
  financial: "Financial",
  behavioral: "Behavioral",
  whistleblower: "Whistleblower",
  account: "Account",
  session: "Session",
  billing: "Billing",
  other: "Other"
};

export default function SupportTicketDashboard() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [responses, setResponses] = useState<TicketResponse[]>([]);
  const [newResponse, setNewResponse] = useState("");
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchTerm, statusFilter, categoryFilter]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to view your support tickets.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast({
        title: "Error loading tickets",
        description: "There was a problem loading your support tickets.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = tickets;

    if (searchTerm) {
      filtered = filtered.filter(ticket =>
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    if (categoryFilter !== "all") {
      filtered = filtered.filter(ticket => ticket.category === categoryFilter);
    }

    setFilteredTickets(filtered);
  };

  const fetchTicketResponses = async (ticketId: string) => {
    try {
      // Get current user for security check
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('ticket_responses')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setResponses(data || []);
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast({
        title: "Error loading responses",
        description: "There was a problem loading ticket responses.",
        variant: "destructive",
      });
    }
  };

  const submitResponse = async () => {
    if (!selectedTicket || !newResponse.trim()) return;

    try {
      setIsSubmittingResponse(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to submit a response.",
          variant: "destructive",
        });
        return;
      }
      
      const { error } = await supabase
        .from('ticket_responses')
        .insert({
          ticket_id: selectedTicket.id,
          user_id: user.id,
          message: newResponse,
          is_internal_note: false,
        });

      if (error) throw error;

      setNewResponse("");
      fetchTicketResponses(selectedTicket.id);
      
      toast({
        title: "Response submitted",
        description: "Your response has been added to the ticket.",
      });
    } catch (error) {
      console.error('Error submitting response:', error);
      toast({
        title: "Error submitting response",
        description: "There was a problem submitting your response.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const openTicketDetail = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    fetchTicketResponses(ticket.id);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="obsidian-card">
            <CardContent className="p-6">
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
                <div className="h-3 bg-muted rounded w-1/4"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <h1 className="text-2xl font-bold text-glow">My Support Tickets</h1>
        <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
          <DialogTrigger asChild>
            <Button className="energy-button">
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
            </DialogHeader>
            <SupportTicketForm 
              onTicketCreated={() => {
                setShowCreateTicket(false);
                fetchTickets();
              }} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="obsidian-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_user">Pending User</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categoryLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <div className="space-y-4">
        {filteredTickets.length === 0 ? (
          <Card className="obsidian-card">
            <CardContent className="p-8 text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No tickets found</h3>
              <p className="text-muted-foreground mb-4">
                {tickets.length === 0 
                  ? "You haven't created any support tickets yet."
                  : "No tickets match your current filters."
                }
              </p>
              <Button 
                onClick={() => setShowCreateTicket(true)}
                className="energy-button"
              >
                Create Your First Ticket
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredTickets.map((ticket) => (
            <Card key={ticket.id} className="obsidian-card hover:bg-muted/20 transition-colors cursor-pointer">
              <CardContent className="p-6" onClick={() => openTicketDetail(ticket)}>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{ticket.title}</h3>
                      {ticket.is_anonymous && (
                        <Badge variant="outline" className="text-xs">Anonymous</Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                      {ticket.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge className={statusColors[ticket.status as keyof typeof statusColors]}>
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <Badge className={priorityColors[ticket.priority as keyof typeof priorityColors]}>
                        {ticket.priority.toUpperCase()}
                      </Badge>
                      <Badge variant="outline">
                        {categoryLabels[ticket.category as keyof typeof categoryLabels]}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(ticket.created_at), 'MMM dd, yyyy')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {format(new Date(ticket.updated_at), 'HH:mm')}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedTicket.title}
                  {selectedTicket.is_anonymous && (
                    <Badge variant="outline" className="text-xs">Anonymous</Badge>
                  )}
                </DialogTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge className={statusColors[selectedTicket.status as keyof typeof statusColors]}>
                    {selectedTicket.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                  <Badge className={priorityColors[selectedTicket.priority as keyof typeof priorityColors]}>
                    {selectedTicket.priority.toUpperCase()}
                  </Badge>
                  <Badge variant="outline">
                    {categoryLabels[selectedTicket.category as keyof typeof categoryLabels]}
                  </Badge>
                </div>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Description</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {responses.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Conversation</h4>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {responses.map((response) => (
                        <div key={response.id} className={`p-3 rounded-lg ${
                          response.is_internal_note 
                            ? 'bg-yellow-500/10 border border-yellow-500/20' 
                            : 'bg-muted/50'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{response.message}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {response.is_internal_note ? 'Admin Note' : 'Response'} • {' '}
                            {format(new Date(response.created_at), 'MMM dd, yyyy HH:mm')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTicket.status !== 'closed' && (
                  <div>
                    <h4 className="font-semibold mb-2">Add Response</h4>
                    <Textarea
                      placeholder="Type your response..."
                      value={newResponse}
                      onChange={(e) => setNewResponse(e.target.value)}
                      className="mb-2"
                    />
                    <Button 
                      onClick={submitResponse}
                      disabled={isSubmittingResponse || !newResponse.trim()}
                      className="energy-button"
                    >
                      {isSubmittingResponse ? "Submitting..." : "Submit Response"}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}